import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  WBBox,
  WBCard,
  WBCardContent,
  WBChip,
  WBFlex,
  WBTypography,
  WBIcon,
  WBIconButton,
  WBDivider,
  WBLink,
  WBLinkButton,
} from '@admiin-com/ds-web';
import { styled, useTheme } from '@mui/material';
import { BillCreateFormData } from '../../../../providers/TaskCreationProvider/TaskCreationProvider';
import { CurrencyNumber } from '../../../../../components/CurrencyNumber/CurrencyNumber';
import { enumToCapitalizedString } from '@admiin-com/ds-common';
import { TaskSettlementStatus } from '@admiin-com/ds-graphql';

const StyledCard = styled(WBCard)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4],
  },
}));

const CardHeader = styled(WBFlex)(({ theme }) => ({
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const InfoRow = styled(WBFlex)(({ theme }) => ({
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(2),
  marginBottom: theme.spacing(1),
}));

interface TaxReviewMobileProps {
  taxes: BillCreateFormData[];
  onEdit: (index: number) => void;
}

export const TaxReviewMobile: React.FC<TaxReviewMobileProps> = ({
  taxes,
  onEdit,
}) => {
  const { t } = useTranslation();

  const getEntityName = (entity: any) => {
    if (!entity) return t('notSpecified', { ns: 'common' });
    return (
      entity.name ||
      entity.companyName ||
      entity.firstName + ' ' + entity.lastName ||
      t('unnamed', { ns: 'common' })
    );
  };

  const getPRN = (tax: BillCreateFormData) => {
    return tax.futureTaskInput?.bpayReferenceNumber || '-';
  };

  const getAmount = (tax: BillCreateFormData) => {
    const amount = tax.futureTaskInput?.amount || 0;
    return amount;
  };

  const getSettlementStatusChip = (settlementStatus?: string | null) => {
    if (!settlementStatus) {
      return (
        <WBTypography variant="body2">
          {t('notProvided', { ns: 'common' })}
        </WBTypography>
      );
    }

    const normalizedStatus = settlementStatus.toString().toUpperCase();
    const color =
      normalizedStatus === TaskSettlementStatus.REFUNDABLE
        ? 'success'
        : normalizedStatus === TaskSettlementStatus.PAYABLE
        ? 'warning'
        : normalizedStatus === TaskSettlementStatus.NIL
        ? 'default'
        : 'default';

    return (
      <WBChip
        label={enumToCapitalizedString(settlementStatus)}
        size="small"
        color={color}
      />
    );
  };

  const theme = useTheme();
  const TaxDocumentCard = ({
    tax,
    index,
  }: {
    tax: BillCreateFormData;
    index: number;
  }) => (
    <StyledCard>
      <WBCardContent>
        <CardHeader>
          <WBTypography variant="h6" fontWeight="bold" mb={0}>
            {tax.reference || t('untitled', { ns: 'common' })}
          </WBTypography>
          <WBLinkButton
            color="primary.main"
            sx={{
              textDecoration: 'underline',
              ...theme.typography.body2,
            }}
            onClick={() => onEdit(index)}
          >
            {t('edit', { ns: 'taskbox' })}
          </WBLinkButton>
        </CardHeader>

        <InfoRow>
          <WBTypography variant="body2" color="text.secondary">
            {t('client', { ns: 'taskbox' })}
          </WBTypography>
          <WBTypography variant="body2" fontWeight="medium" textAlign={'right'}>
            {getEntityName(tax.to)}
          </WBTypography>
        </InfoRow>

        <InfoRow>
          <WBTypography variant="body2" color="text.secondary">
            {t('prn', { ns: 'taskbox' })}
          </WBTypography>
          <WBTypography variant="body2" fontFamily="monospace">
            {getPRN(tax)}
          </WBTypography>
        </InfoRow>

        <InfoRow>
          <WBTypography variant="body2" color="text.secondary">
            {t('documentType', { ns: 'taskbox' })}
          </WBTypography>
          <WBTypography variant="body2">
            {tax.documentType || t('notProvided', { ns: 'common' })}
          </WBTypography>
        </InfoRow>

        <InfoRow>
          <WBTypography variant="body2" color="text.secondary">
            {t('settlementStatus', { ns: 'taskbox' })}
          </WBTypography>
          {getSettlementStatusChip(tax.settlementStatus)}
        </InfoRow>

        <WBDivider sx={{ my: 2 }} />

        <InfoRow>
          <WBTypography variant="h6" fontWeight="bold">
            {t('amount', { ns: 'taskbox' })}
          </WBTypography>
          <CurrencyNumber
            number={getAmount(tax)}
            variant="h6"
            sup={false}
            fontWeight="bold"
          />
        </InfoRow>
      </WBCardContent>
    </StyledCard>
  );

  return (
    <WBBox mb={3}>
      {taxes.map((tax, index) => (
        <TaxDocumentCard key={index} tax={tax} index={index} />
      ))}
    </WBBox>
  );
};
